import axios from 'axios';

// Environment-aware API base URL
const getApiBaseUrl = () => {
  // Check if we're in development or production
  if (process.env.NODE_ENV === 'development') {
    // In development, use the proxy to localhost:3000
    return 'http://localhost:3000';
  } else {
    // In production, use the environment variable or default to current origin
    // The web.config proxies /api/* requests to the backend, so we use the origin directly
    return process.env.REACT_APP_API_URL || window.location.origin;
  }
};

const API_BASE_URL = getApiBaseUrl();

console.log('API Service Configuration:');
console.log('Environment:', process.env.NODE_ENV);
console.log('API Base URL:', API_BASE_URL);
console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);

// Axios instance with environment-aware configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Let browser/Axios set multipart boundary automatically for FormData uploads.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers?.common) delete config.headers.common['Content-Type'];
      if (config.headers?.post) delete config.headers.post['Content-Type'];
      if (config.headers?.put) delete config.headers.put['Content-Type'];
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Generic request wrapper
const request = async (method, url, data = null, config = {}) => {
  try {
    const token = localStorage.getItem('token');
    const headers = {
      Authorization: token ? `Bearer ${token}` : undefined,
      ...config.headers,
    };

    const fullUrl = API_BASE_URL ? `${API_BASE_URL}${url}` : url;
    console.log(`API Request: ${method.toUpperCase()} ${url}`, {
      baseURL: API_BASE_URL,
      fullUrl: fullUrl,
      headers: headers,
      data: data
    });

    const response = await api({
      method,
      url,
      data,
      ...config,
      headers,
    });

    console.log(`API Response: ${method.toUpperCase()} ${url}`, response.data);
    return response.data;
  } catch (err) {
    console.error(`API Error: ${method.toUpperCase()} ${url}`, {
      message: err.message,
      response: err.response?.data,
      status: err.response?.status,
      config: err.config
    });
    
    if (err.response && err.response.data && err.response.data.message) {
      throw new Error(err.response.data.message);
    }
    throw err;
  }
};

// CRUD methods
export const get = (url, config) => request('get', url, null, config);
export const post = (url, data, config) => request('post', url, data, config);
export const put = (url, data, config) => request('put', url, data, config);
export const del = (url, config) => request('delete', url, undefined, config);
export const deleteRequest = (url, config) => request('delete', url, undefined, config);

// ----------------- File handling -----------------

/**
 * Open a file in a new tab
 * @param {string|File} file - Either a File object (new upload) or URL path from server
 */
export const viewFile = (file) => {
  if (!file) return;

  if (file instanceof File) {
    const fileUrl = URL.createObjectURL(file);
    window.open(fileUrl, '_blank');
  } else {
    const fullUrl = file.startsWith('http')
      ? file
      : `${API_BASE_URL}${file.startsWith('/') ? '' : '/'}${file}`;
    window.open(fullUrl, '_blank');
  }
};

/**
 * Download a file from the server
 * @param {string} fileUrl - Path returned from backend
 * @param {string} filename - Optional filename
 */
export const downloadFile = (fileUrl, filename = 'file') => {
  if (!fileUrl) return;

  const fullUrl = fileUrl.startsWith('http')
    ? fileUrl
    : `${API_BASE_URL}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;

  fetch(fullUrl, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
  })
    .then((res) => res.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    })
    .catch((err) => console.error('Error downloading file:', err));
};

/**
 * Upload a file with progress tracking
 * @param {string} url - Upload endpoint
 * @param {FormData} formData - Form data containing file
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise} Upload response
 */
export const uploadFile = async (url, formData, onProgress = null) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.post(url, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      onUploadProgress: onProgress ? (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      } : undefined,
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

// ----------------- Auth methods -----------------

/**
 * Login user
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise} Login response
 */
export const login = async (email, password) => {
  return post('/api/auth/login', { email, password });
};

/**
 * Verify authentication token
 * @returns {Promise} Token verification response
 */
export const verifyToken = async () => {
  return get('/api/auth/verify-token');
};

/**
 * Logout user
 * @returns {Promise} Logout response
 */
export const logout = async () => {
  try {
    await post('/api/auth/logout');
  } finally {
    localStorage.removeItem('token');
  }
};

// ----------------- Contract methods -----------------

/**
 * Get all contracts
 * @param {Object} filters - Optional filters
 * @returns {Promise} Contracts list
 */
export const getContracts = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const url = params ? `/api/contracts?${params}` : '/api/contracts';
  return get(url);
};

/**
 * Get contract by ID
 * @param {number} id - Contract ID
 * @returns {Promise} Contract data
 */
export const getContract = async (id) => {
  return get(`/api/contracts/${id}`);
};

/**
 * Create new contract
 * @param {FormData} formData - Contract data with file
 * @param {Function} onProgress - Upload progress callback
 * @returns {Promise} Created contract
 */
export const createContract = async (formData, onProgress = null) => {
  return uploadFile('/api/contracts', formData, onProgress);
};

/**
 * Update contract
 * @param {number} id - Contract ID
 * @param {FormData} formData - Updated contract data
 * @param {Function} onProgress - Upload progress callback
 * @returns {Promise} Updated contract
 */
export const updateContract = async (id, formData, onProgress = null) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.put(`/api/contracts/${id}`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      onUploadProgress: onProgress ? (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      } : undefined,
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Delete contract
 * @param {number} id - Contract ID
 * @returns {Promise} Deletion response
 */
export const deleteContract = async (id) => {
  return deleteRequest(`/api/contracts/${id}`);
};

// ----------------- Policy version control (Policy doc type only) -----------------

/**
 * List policy versions for a contract
 * @param {number} contractId - Contract ID (must be type Policy)
 * @returns {Promise} Array of version summaries
 */
export const getPolicyVersions = async (contractId) => {
  return get(`/api/contracts/${contractId}/versions`);
};

/**
 * Get a single policy version snapshot
 * @param {number} contractId - Contract ID
 * @param {number} versionNumber - Version number
 * @returns {Promise} Version snapshot data
 */
export const getPolicyVersion = async (contractId, versionNumber) => {
  return get(`/api/contracts/${contractId}/versions/${versionNumber}`);
};

/**
 * Restore a policy to a previous version
 * @param {number} contractId - Contract ID
 * @param {number} versionNumber - Version number to restore to
 * @returns {Promise} Response message
 */
export const restorePolicyVersion = async (contractId, versionNumber) => {
  return post(`/api/contracts/${contractId}/versions/${versionNumber}/restore`);
};

// ----------------- User management methods -----------------

/**
 * Get all users
 * @returns {Promise} Users list
 */
export const getUsers = async () => {
  return get('/api/users');
};

/**
 * Create user
 * @param {Object} userData - User data
 * @returns {Promise} Created user
 */
export const createUser = async (userData) => {
  return post('/api/users', userData);
};

/**
 * Update user
 * @param {number} id - User ID
 * @param {Object} userData - Updated user data
 * @returns {Promise} Updated user
 */
export const updateUser = async (id, userData) => {
  return put(`/api/users/${id}`, userData);
};

/**
 * Delete user
 * @param {number} id - User ID
 * @returns {Promise} Deletion response
 */
export const deleteUser = async (id) => {
  return deleteRequest(`/api/users/${id}`);
};

/**
 * Change user password
 * @param {number} id - User ID
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise} Password change response
 */
export const changePassword = async (id, currentPassword, newPassword) => {
  return put(`/api/users/${id}/password`, {
    currentPassword,
    newPassword
  });
};

// ----------------- Role management methods -----------------

/**
 * Get all roles
 * @returns {Promise} Roles list
 */
export const getRoles = async () => {
  return get('/api/roles');
};

/**
 * Get role permissions
 * @returns {Promise} Permissions list
 */
export const getRolePermissions = async () => {
  return get('/api/roles/permissions');
};

/**
 * Create role
 * @param {Object} roleData - Role data
 * @returns {Promise} Created role
 */
export const createRole = async (roleData) => {
  return post('/api/roles', roleData);
};

/**
 * Update role
 * @param {number} id - Role ID
 * @param {Object} roleData - Updated role data
 * @returns {Promise} Updated role
 */
export const updateRole = async (id, roleData) => {
  return put(`/api/roles/${id}`, roleData);
};

/**
 * Delete role
 * @param {number} id - Role ID
 * @returns {Promise} Deletion response
 */
export const deleteRole = async (id) => {
  return deleteRequest(`/api/roles/${id}`);
};

// ----------------- Department methods -----------------

/**
 * Get all departments
 * @returns {Promise} Departments list
 */
export const getDepartments = async () => {
  return get('/api/departments');
};

/**
 * Create department
 * @param {Object} departmentData - Department data
 * @returns {Promise} Created department
 */
export const createDepartment = async (departmentData) => {
  return post('/api/departments', departmentData);
};

/**
 * Update department
 * @param {number} id - Department ID
 * @param {Object} departmentData - Updated department data
 * @returns {Promise} Updated department
 */
export const updateDepartment = async (id, departmentData) => {
  return put(`/api/departments/${id}`, departmentData);
};

/**
 * Delete department
 * @param {number} id - Department ID
 * @returns {Promise} Deletion response
 */
export const deleteDepartment = async (id) => {
  return deleteRequest(`/api/departments/${id}`);
};

// ----------------- Region methods -----------------

/**
 * Get all regions
 * @returns {Promise} Regions list
 */
export const getRegions = async () => {
  return get('/api/regions');
};

/**
 * Create region
 * @param {Object} regionData - Region data
 * @returns {Promise} Created region
 */
export const createRegion = async (regionData) => {
  return post('/api/regions', regionData);
};

/**
 * Update region
 * @param {number} id - Region ID
 * @param {Object} regionData - Updated region data
 * @returns {Promise} Updated region
 */
export const updateRegion = async (id, regionData) => {
  return put(`/api/regions/${id}`, regionData);
};

/**
 * Delete region
 * @param {number} id - Region ID
 * @returns {Promise} Deletion response
 */
export const deleteRegion = async (id) => {
  return deleteRequest(`/api/regions/${id}`);
};

// ----------------- Import methods -----------------

/**
 * Import contracts from Excel file
 * @param {File} file - Excel file to import
 * @param {Function} onProgress - Upload progress callback
 * @returns {Promise} Import response
 */
export const importContractsFromExcel = async (file, onProgress = null) => {
  const formData = new FormData();
  formData.append('file', file);
  
  try {
    const token = localStorage.getItem('token');
    const response = await api.post('/api/contracts/import/excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`,
      },
      onUploadProgress: onProgress ? (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      } : undefined,
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Download import template
 * @returns {Promise} Template download response
 */
export const downloadImportTemplate = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get('/api/contracts/import/template', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      responseType: 'blob',
    });
    
    // Create download link
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'contract_import_template.xlsx';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    return { success: true };
  } catch (error) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

// ----------------- AI Agent -----------------

/**
 * List agent knowledge (Q&A and documents)
 * @returns {Promise} List of knowledge entries
 */
export const getAgentKnowledge = async () => {
  return get('/api/agent/knowledge');
};

/**
 * Add knowledge: Q&A pair or document
 * @param {Object} data - { type: 'qa'|'document', question?, answer?, content? }
 * @returns {Promise} Created entry
 */
export const addAgentKnowledge = async (data) => {
  return post('/api/agent/knowledge', data);
};

/**
 * Delete a knowledge entry
 * @param {number} id - Knowledge entry ID
 * @returns {Promise}
 */
export const deleteAgentKnowledge = async (id) => {
  return deleteRequest(`/api/agent/knowledge/${id}`);
};

/**
 * Ask the AI agent a question (uses trained knowledge)
 * @param {string} question
 * @returns {Promise} { answer, question }
 */
export const askAgent = async (question) => {
  return post('/api/agent/ask', { question });
};

// ----------------- Health check -----------------

/**
 * Check server health
 * @returns {Promise} Health status
 */
export const checkHealth = async () => {
  return get('/api/health');
};

// Export default Axios instance if needed
export default api;
